import React, { useState } from 'react';
import { CRM_CONSTANTS } from '../../utils/crmConstants';

function InternalNotesPanel({ ticket, notes, onAddNote }) {
  const [noteText, setNoteText] = useState('');
  const [taggedUsers, setTaggedUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [cursorPosition, setCursorPosition] = useState(0);
  const itemsPerPage = 5;

  // Mock HR team members (in production, fetch from API)
  const hrTeamMembers = [
    { email: 'hr1@cafuamanagement.com', name: 'HR Manager 1' },
    { email: 'hr2@cafuamanagement.com', name: 'HR Manager 2' },
    { email: 'hr3@cafuamanagement.com', name: 'HR Specialist' },
    { email: 'admin@cafuamanagement.com', name: 'Admin' }
  ];

  const handleNoteChange = (e) => {
    const text = e.target.value;
    setNoteText(text);
    setCursorPosition(e.target.selectionStart);

    // Check for @ mentions
    const lastAtIndex = text.lastIndexOf('@', cursorPosition);
    if (lastAtIndex !== -1) {
      const afterAt = text.substring(lastAtIndex + 1, cursorPosition);
      if (!afterAt.includes(' ') && afterAt.length < 30) {
        setShowMentionDropdown(true);
      } else {
        setShowMentionDropdown(false);
      }
    } else {
      setShowMentionDropdown(false);
    }
  };

  const handleMentionSelect = (user) => {
    const lastAtIndex = noteText.lastIndexOf('@', cursorPosition);
    const beforeAt = noteText.substring(0, lastAtIndex);
    const afterCursor = noteText.substring(cursorPosition);
    const newText = `${beforeAt}@${user.name} ${afterCursor}`;

    setNoteText(newText);
    setShowMentionDropdown(false);

    if (!taggedUsers.includes(user.email)) {
      setTaggedUsers([...taggedUsers, user.email]);
    }
  };

  const handleSaveNote = async () => {
    if (!noteText.trim()) return;

    setSaving(true);
    try {
      await onAddNote({
        note_content: noteText,
        created_by_email: localStorage.getItem('userEmail') || 'hr@cafuamanagement.com',
        created_by_name: localStorage.getItem('userName') || 'HR User',
        tagged_users: taggedUsers, // Array of emails
      });
      setNoteText('');
      setTaggedUsers([]);
    } catch (err) {
      console.error('Failed to save note:', err);
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const highlightMentions = (text) => {
    if (!text) return text;

    // Replace @mentions with highlighted spans
    return text.split(/(@\w+\s?\w+)/g).map((part, index) => {
      if (part.startsWith('@')) {
        return (
          <span key={index} className="mention-highlight">
            {part}
          </span>
        );
      }
      return part;
    });
  };

  // Pagination
  const paginatedNotes = notes.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const totalPages = Math.ceil(notes.length / itemsPerPage);

  if (!ticket) return null;

  return (
    <div className="internal-notes-panel">
      {/* Note Composer Section */}
      <div className="notes-composer-section">
        <h3>Internal Notes</h3>
        <p className="help-text">Add internal notes for your team. Use @ to mention team members.</p>

        <div className="form-group">
          <div className="note-editor-wrapper">
            <textarea
              value={noteText}
              onChange={handleNoteChange}
              className="control-textarea"
              rows="6"
              placeholder="Type your note here... Use @ to mention team members"
              maxLength={CRM_CONSTANTS.FIELD_LENGTHS.NOTE}
            />

            {/* @Mention Dropdown */}
            {showMentionDropdown && (
              <div className="mention-dropdown">
                {hrTeamMembers.map((user, index) => (
                  <div
                    key={index}
                    className="mention-option"
                    onClick={() => handleMentionSelect(user)}
                  >
                    <div className="mention-info">
                      <div className="mention-name">{user.name}</div>
                      <div className="mention-email">{user.email}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="note-meta">
            <span className="char-count">{noteText.length} / {CRM_CONSTANTS.FIELD_LENGTHS.NOTE}</span>
            {taggedUsers.length > 0 && (
              <span className="tagged-info">
                Tagged: {taggedUsers.join(', ')}
              </span>
            )}
          </div>
        </div>

        {/* Formatting Toolbar (simplified) */}
        <div className="note-toolbar">
          <button type="button" className="toolbar-btn" title="Bold"><strong>B</strong></button>
          <button type="button" className="toolbar-btn" title="Italic"><em>I</em></button>
          <button type="button" className="toolbar-btn" title="Bullet List">• List</button>
        </div>

        <div className="form-actions">
          <button
            onClick={handleSaveNote}
            disabled={saving || !noteText.trim()}
            className="btn-primary"
          >
            {saving ? 'Saving...' : 'Save Note'}
          </button>
        </div>
      </div>

      {/* Internal Note History Section */}
      <div className="notes-history-section">
        <h3>Internal Note History</h3>

        {notes.length === 0 ? (
          <div className="empty-state">
            <p>No internal notes yet</p>
          </div>
        ) : (
          <>
            <table className="notes-table">
              <thead>
                <tr>
                  <th>Internal Note</th>
                  <th>Created By</th>
                  <th>Created At</th>
                </tr>
              </thead>
              <tbody>
                {paginatedNotes.map((note, index) => (
                  <tr key={index}>
                    <td className="note-content-cell">
                      {highlightMentions(note.note_content)}
                      {note.tagged_users && note.tagged_users.length > 0 && (
                        <div className="tagged-users-chips">
                          {note.tagged_users.map((user, i) => (
                            <span key={i} className="tagged-user-chip">
                              @{user.name || user.email || user}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="user-info">
                        {note.created_by_name || note.created_by_email || note.created_by || 'Unknown'}
                        {note.created_by_email && <div className="user-email">{note.created_by_email}</div>}
                      </div>
                    </td>
                    <td>{formatDate(note.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="btn-pagination"
                >
                  Previous
                </button>
                <span className="page-info">Page {currentPage} of {totalPages}</span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="btn-pagination"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default InternalNotesPanel;
